/**
 * The team page's binding of the shared detail-page surface.
 *
 * Everything structural — the shell, the drag, the rail, the persistence —
 * lives in `shared/surface`. This file is the whole of what a team adds: its
 * config (`./layout`) and one typed hook over the generic one.
 *
 * It binds the SELECTION-FREE hook: a team page has no detail pane, so it
 * neither names a selection kind nor publishes a `select` that would open a
 * pane it never declared.
 *
 * The ORG PANE SET lives here too and is shared with departments — a team and
 * a department are the same record with a different noun, and the two detail
 * pages were near-identical 870-line files. See `./OrgContextPane` and
 * `./orgMembers`.
 */

import {
  useSurfaceWithoutSelection,
  UseDetailSurfaceOptions,
  UseSurfaceWithoutSelectionResult,
} from '../../../shared/surface/useDetailSurface';
import { TEAM_SURFACE, TEAM_TABS, TeamPanelId, TeamTab } from './layout';
import { OrgReaderContent } from '../../Shared/OrgSurfaceContext';

export { TEAM_SURFACE, TEAM_TABS };
export type { TeamPanelId, TeamTab };

export type TeamWorkspace = UseSurfaceWithoutSelectionResult<TeamPanelId, OrgReaderContent, TeamTab>;

/**
 * The team page's layout and tab state.
 *
 * Takes the team uuid because the arrangement is remembered per team — see
 * the note at the top of `./layout`.
 */
export const useTeamWorkspace = (
  teamId: string,
  options: Omit<UseDetailSurfaceOptions<TeamTab>, 'entityId'> = {}
): TeamWorkspace =>
  useSurfaceWithoutSelection<TeamPanelId, OrgReaderContent, TeamTab>(TEAM_SURFACE, {
    ...options,
    entityId: teamId,
  });
